/**
 * Client-side error reporter.
 *
 * Hooks window.onerror + unhandledrejection and POSTs a sanitized
 * payload to `/api/client-error` on the server, which logs it to a
 * rolling JSONL file (data/client-errors.jsonl). The admin dashboard
 * surfaces the latest errors so the operator finally has visibility
 * into JS exceptions that real users hit but never report.
 *
 * Without this hook, the server can see deaths/leaves but has NO way
 * to tell that a player loaded the page, got a TypeError before they
 * could click PLAY, and bounced — all those failures look identical
 * to "user just wasn't interested" in the analytics.
 *
 * Sampling/deduping rules to avoid log floods:
 *   - Same (message, stack-first-line) within 30 s → dedupe to 1 send
 *   - Hard cap of 50 errors per page load → defense against a render
 *     loop that throws on every frame
 *   - Filenames/stack lines are truncated to 200 chars each
 *
 * Privacy: no PII collected. We send message, stack, url, ua, viewport
 * size. The sessionId (if available from Colyseus) is included so the
 * operator can correlate an error with a specific session in the
 * sessions log.
 */
import { getLocalSessionId } from './serverWorld';

const ENDPOINT = '/api/client-error';
const HARD_CAP_PER_PAGE = 50;
const DEDUPE_WINDOW_MS = 30_000;

interface ErrorPayload {
  ts: number;
  kind: 'error' | 'unhandledrejection';
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  col?: number;
  url: string;
  userAgent: string;
  viewport: { w: number; h: number };
  sessionId: string | null;
}

let sentCount = 0;
const recentSignatures = new Map<string, number>();

function signatureOf(p: ErrorPayload): string {
  const stackHead = (p.stack ?? '').split('\n')[0] ?? '';
  return `${p.kind}|${p.message}|${stackHead}`.slice(0, 400);
}

async function report(p: ErrorPayload): Promise<void> {
  if (sentCount >= HARD_CAP_PER_PAGE) return;
  const sig = signatureOf(p);
  const lastSeen = recentSignatures.get(sig) ?? 0;
  if (p.ts - lastSeen < DEDUPE_WINDOW_MS) return;
  recentSignatures.set(sig, p.ts);
  sentCount++;
  try {
    // Use sendBeacon when available (survives page-unload) and fall
    // back to fetch for older browsers / cases where Beacon refuses
    // due to size or unsupported MIME.
    const body = JSON.stringify(p);
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    // Silent — the reporter mustn't itself throw and feedback-loop.
  }
}

function truncate(s: string | undefined, max = 200): string | undefined {
  if (!s) return undefined;
  return s.length > max ? s.slice(0, max) + '…' : s;
}

export function installErrorReporter(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event: ErrorEvent) => {
    void report({
      ts: Date.now(),
      kind: 'error',
      message: truncate(event.message) ?? 'unknown error',
      stack: truncate(event.error?.stack, 1200),
      source: truncate(event.filename),
      line: event.lineno,
      col: event.colno,
      url: location.href.slice(0, 300),
      userAgent: navigator.userAgent.slice(0, 240),
      viewport: { w: window.innerWidth, h: window.innerHeight },
      sessionId: getLocalSessionId(),
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason as unknown;
    let message = 'unhandled rejection';
    let stack: string | undefined;
    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack;
    } else if (typeof reason === 'string') {
      message = reason;
    } else if (reason && typeof reason === 'object') {
      try { message = JSON.stringify(reason).slice(0, 300); } catch { /* noop */ }
    }
    void report({
      ts: Date.now(),
      kind: 'unhandledrejection',
      message: truncate(message) ?? 'unhandled rejection',
      stack: truncate(stack, 1200),
      url: location.href.slice(0, 300),
      userAgent: navigator.userAgent.slice(0, 240),
      viewport: { w: window.innerWidth, h: window.innerHeight },
      sessionId: getLocalSessionId(),
    });
  });
}
